import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Moon, ArrowLeft, BookOpen, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchKBCategory } from "@/lib/api";
import NavBar from "@/components/NavBar";

const CATEGORY_COLORS = {
  planets: "border-amber-500/20 hover:border-amber-500/40",
  houses: "border-indigo-500/20 hover:border-indigo-500/40",
  signs: "border-emerald-500/20 hover:border-emerald-500/40",
  nakshatras: "border-pink-500/20 hover:border-pink-500/40",
  foundations: "border-cyan-500/20 hover:border-cyan-500/40",
  "lunar-nodes": "border-violet-500/20 hover:border-violet-500/40",
  "planetary-strength": "border-orange-500/20 hover:border-orange-500/40",
  yogas: "border-purple-500/20 hover:border-purple-500/40",
  aspects: "border-sky-500/20 hover:border-sky-500/40",
  dashas: "border-rose-500/20 hover:border-rose-500/40",
  transits: "border-red-500/20 hover:border-red-500/40",
  vargas: "border-emerald-500/20 hover:border-emerald-500/40",
  "nakshatra-padas": "border-teal-500/20 hover:border-teal-500/40",
  "karmic-astrology": "border-violet-500/20 hover:border-violet-500/40",
};

const CATEGORY_ACCENTS = {
  planets: "text-amber-400",
  houses: "text-indigo-400",
  signs: "text-emerald-400",
  nakshatras: "text-pink-400",
  foundations: "text-cyan-400",
  "lunar-nodes": "text-violet-400",
  "planetary-strength": "text-orange-400",
  yogas: "text-purple-400",
  aspects: "text-sky-400",
  dashas: "text-rose-400",
  transits: "text-red-400",
  vargas: "text-emerald-400",
  "nakshatra-padas": "text-teal-400",
  "karmic-astrology": "text-violet-400",
};

export default function ArticleList() {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    fetchKBCategory(categorySlug)
      .then((d) => {
        setCategory(d.category);
        setArticles(d.articles);
      })
      .catch(() => navigate("/learn"))
      .finally(() => setLoading(false));
  }, [categorySlug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <img src="/logo.png" alt="Daily Nakshatra" className="h-10 w-auto logo-pulse" />
      </div>
    );
  }

  const accent = CATEGORY_ACCENTS[categorySlug] || "text-white/70";
  const borderClass = CATEGORY_COLORS[categorySlug] || "border-white/10 hover:border-white/20";

  return (
    <div className="min-h-screen bg-[#050505]">
      <NavBar />

      <main className="pt-14 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 pt-8 pb-2 text-xs text-white/30 font-[Manrope]" data-testid="article-list-breadcrumb">
          <Link to="/learn" className="hover:text-white/60 transition-colors">Learn</Link>
          <ChevronRight className="w-3 h-3" />
          <span className={accent}>{category?.title}</span>
        </div>

        {/* Header */}
        <div className="pb-8 pt-2" data-testid="article-list-header">
          <Button variant="ghost" size="sm" className="text-white/30 hover:text-white/60 mb-4 -ml-2 text-xs" onClick={() => navigate("/learn")}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All Topics
          </Button>
          <h1 className="text-3xl sm:text-4xl font-light text-white/95 mb-2 font-[Manrope]">{category?.title}</h1>
          <p className="text-base text-white/40 font-[Manrope]">{category?.description}</p>
        </div>

        {/* Nakshatra category special link */}
        {categorySlug === "nakshatras" && (
          <Link
            to="/nakshatras"
            className="flex items-center gap-3 mb-6 p-4 rounded-xl border border-pink-500/20 bg-pink-500/5 hover:bg-pink-500/10 transition-colors group"
            data-testid="nakshatras-gallery-link"
          >
            <ExternalLink className="w-4 h-4 text-pink-400/60" />
            <div className="flex-1">
              <p className="text-sm text-white/80 font-medium font-[Manrope]">Explore all 27 Nakshatras</p>
              <p className="text-xs text-white/30 font-[Manrope]">View the complete nakshatra gallery with detailed profiles</p>
            </div>
            <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/50 transition-colors" />
          </Link>
        )}

        {/* Articles */}
        <div className="space-y-3 pb-20" data-testid="article-list-grid">
          {articles.map((art, i) => (
            <Link
              key={art.slug}
              to={`/learn/article/${art.slug}`}
              className={`group flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 bg-white/[0.02] ${borderClass}`}
              data-testid={`article-card-${art.slug}`}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.04] text-white/25 text-sm font-medium font-[Manrope] shrink-0">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base text-white/90 font-medium font-[Manrope] truncate">{art.title}</h3>
                <p className="text-xs text-white/35 font-[Manrope] truncate">{art.subtitle}</p>
              </div>
              <div className="hidden sm:flex flex-wrap gap-1 max-w-[200px]">
                {(art.keywords || []).slice(0, 3).map((kw) => (
                  <span key={kw} className="text-[10px] px-2 py-0.5 rounded-full border border-white/8 text-white/20 font-[Manrope]">{kw}</span>
                ))}
              </div>
              <ChevronRight className="w-4 h-4 text-white/15 group-hover:text-white/40 transition-colors shrink-0" />
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
